/**
 * ByRoomCalendar — Block
 *
 * Time-row × room-column grid. Each clinical room (e.g. "Room 512",
 * "Room 305") gets a column; each hour of the working day a row.
 * Used by reception to see room utilisation and avoid double-booking
 * physical space.
 *
 * Flutter parity: `calendar_by_room_widget.dart`.
 *
 * Room set strategy (PROD-985 — show all rooms):
 *   The caller passes the practice's full exam-room list via `rooms`
 *   (from `useExamRooms` → `GET /api/Practice/EditPracticeExamRoom`), so
 *   rooms with ZERO appointments today still get a column — matching
 *   Flutter, which renders one column per `ExamRoom` regardless of
 *   occupancy (`calendar_by_room_widget.dart`). `rowsByRoom` (derived
 *   from the appointment payload via `groupRowsByRoom`) is then bucketed
 *   into those columns. `resolveColumnRooms` unions the two: explicit
 *   rooms first (their order), then any appointment room NOT in the
 *   exam list appended alphabetically. (Flutter drops off-list
 *   appointments entirely; the union is a deliberate improvement — it
 *   never hides an appointment that lives in a stale/renamed room.)
 *   When no explicit list is passed (e.g. the exam-room fetch is still
 *   in flight, or Storybook fixtures), columns fall back to the
 *   appointment-derived set.
 *
 * Empty / loading:
 *   - `isLoading` → shimmering placeholder grid (in-flight refetch isn't
 *     mistaken for a truly empty room set).
 *   - Zero columns (no exam rooms configured AND no appointments) → a
 *     friendly message instead of an empty grid.
 */

import { useRef } from 'react';
import { TimeLabel } from '@/components/atoms';
import { locationTileBg, locationEdgeColor } from '@/scheduling/lib/location-colors';
import { StatusBadge, type AppointmentStatusType } from '@/components/atoms';
import { cn } from '@/lib/utils';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import { computeOverlapLayout, slotToPercents } from '@/scheduling/lib/overlap-layout';
import { CurrentTimeLine } from '@/components/blocks/current-time-line';
import { ColumnsCalendarSkeleton } from '@/components/blocks/calendar-skeleton';
import { GutterClock } from '@/components/blocks/gutter-clock';
import { useScrollToNow } from '@/components/blocks/use-scroll-to-now';
import { AppointmentTileMeta } from '@/components/blocks/appointment-tile-meta';

/** Minute-of-day range for a DayListRow, for overlap layout (PROD-982). */
function rowMinutes(row: DayListRow): { startMin: number; endMin: number } {
  const s = new Date(row.startISO);
  const e = new Date(row.endISO);
  return {
    startMin: s.getHours() * 60 + s.getMinutes(),
    endMin: e.getHours() * 60 + e.getMinutes(),
  };
}

const HOUR_HEIGHT = 60;

interface ByRoomCalendarProps {
  /**
   * Ordered room names — drives column order. If absent, derived from
   * the rows themselves (every distinct `row.room` value).
   *
   * When BOTH `rooms` and `rowsByRoom` carry entries, the resulting
   * column set is the UNION — explicit rooms keep their order, then
   * any leftover rooms from the row data are appended alphabetically.
   * Dedup is case-insensitive (Samer review #90 §2 — FHIR Location.name
   * is user-entered; "Room 1" vs "room 1" should collapse to one
   * column).
   */
  rooms?: string[];
  /** Map of room name → ordered list of appointments in that room. */
  rowsByRoom: Map<string, DayListRow[]>;
  hours?: number[];
  selectedDate: Date;
  /**
   * True while the dayListRows source query is in flight. Renders a
   * shimmering placeholder grid so an in-flight refetch isn't
   * indistinguishable from a truly empty room set. (Samer review
   * #90 §4.)
   */
  isLoading?: boolean;
  onAppointmentClick?: (appointmentId: string) => void;
}

export function ByRoomCalendar({
  rooms,
  rowsByRoom,
  hours = defaultHours(),
  selectedDate,
  isLoading = false,
  onAppointmentClick,
}: ByRoomCalendarProps) {
  // Resolve column set: union of explicit list + derived rooms,
  // case-insensitive dedup. Explicit list preserves its caller-defined
  // order; derived overflow is appended alphabetically.
  const columnRooms = resolveColumnRooms(rooms, rowsByRoom);

  const startHour = hours[0] ?? 8;
  const endHour = (hours[hours.length - 1] ?? 17) + 1;

  // Scroll to current hour on mount. Hooks stay above the early returns: below them the view crashed once the
  // columns loaded ("Rendered more hooks than during the previous render", 15 Oct). Before the grid mounts the ref is
  // empty, so nothing latches until it does.
  const roomScrollRef = useRef<HTMLDivElement>(null);
  useScrollToNow(roomScrollRef, { startHour, endHour, hourHeight: HOUR_HEIGHT, key: selectedDate.toDateString() });

  // Loading state — render the dateLabel + a pulsing empty grid so
  // the user sees "loading" not "empty". Cells don't render because
  // we have no real room data yet.
  if (isLoading) {
    return <ByRoomCalendarSkeleton selectedDate={selectedDate} hours={hours} columns={columnRooms.length || 3} />;
  }

  if (columnRooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-text-muted">
        No exam rooms configured for this practice.
      </div>
    );
  }

  // Current-time line (PROD-981) shows only when the displayed day is today.
  const isShowingToday = selectedDate.toDateString() === new Date().toDateString();
  const dateLabel = selectedDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-surface-card">
      <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
        <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
          By room · {dateLabel}
        </p>
        <p className="text-sm text-text-muted">
          {columnRooms.length} room{columnRooms.length === 1 ? '' : 's'}
        </p>
      </div>

      <div ref={roomScrollRef} data-cal-scroll className="scrollbar-hide">
       <div data-cal-sheet>
        {/* Room header pinned top, its clock corner pinned left too (showcase-bridge.css). */}
        <div data-cal-head className="flex h-10 items-center border-b border-border-default">
          {/* PROD-1797 — the gutter header was empty here while Day showed the
              live clock + GMT offset. Client-reported. Same component in all
              views so the label can never disagree between them. */}
          <GutterClock data-cal-gutter className="w-16 flex-shrink-0 border-r border-border-subtle px-2 py-1" />
          {columnRooms.map((room) => {
            const count = rowsByRoom.get(room)?.length ?? 0;
            return (
              <div
                key={room}
                className="flex-1 min-w-[144px] border-r border-border-subtle last:border-r-0 px-3 py-2"
              >
                <p className="text-sm font-semibold text-text-primary truncate">
                  {room}
                </p>
                <p className="text-xs text-text-muted">
                  {count} appt{count === 1 ? '' : 's'}
                </p>
              </div>
            );
          })}
        </div>

        <div className="relative">
          {hours.map((hour) => (
            <div
              key={hour}
              className="flex border-b border-border-subtle"
              style={{ height: HOUR_HEIGHT }}
            >
              <div data-cal-gutter className="w-16 flex-shrink-0 px-2 pt-1 border-r border-border-subtle">
                <TimeLabel hour={hour} />
              </div>
              {columnRooms.map((room) => (
                <div
                  key={room}
                  className="flex-1 min-w-[144px] border-r border-border-subtle last:border-r-0"
                />
              ))}
            </div>
          ))}

          <div
            className="absolute inset-0 pl-16 grid pointer-events-none"
            style={{
              gridTemplateColumns: `repeat(${columnRooms.length}, minmax(140px, 1fr))`,
            }}
          >
            {columnRooms.map((room) => {
              const rows = rowsByRoom.get(room) ?? [];
              // Side-by-side layout for overlapping appointments in this
              // room column (PROD-982).
              const items = rows.map((row) => ({ row, ...rowMinutes(row) }));
              const layout = computeOverlapLayout(items);
              return (
                <div key={room} className="relative">
                  {items.map((it) => {
                    const slot = layout.get(it);
                    const { leftPct, widthPct } =
                        slot && slot.columnCount > 1
                          ? slotToPercents(slot)
                      : { leftPct: undefined, widthPct: undefined };
                    return (
                      <AppointmentTileSimple
                        key={it.row.id}
                        row={it.row}
                        startHour={startHour}
                        endHour={endHour}
                        leftPct={leftPct}
                        widthPct={widthPct}
                        onClick={onAppointmentClick}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
          {/* Current-time "now" line (PROD-981) — spans all room columns,
              offset past the time gutter; today only. */}
          {isShowingToday ? (
            <CurrentTimeLine
              startHour={startHour}
              endHour={endHour}
              hourHeight={HOUR_HEIGHT}
              className="left-16 right-0"
            />
          ) : null}
        </div>
       </div>
      </div>
    </div>
  );
}

interface AppointmentTileSimpleProps {
  row: DayListRow;
  startHour: number;
  endHour: number;
  /** Overlap geometry (PROD-982) — side-by-side placement within the column. */
  leftPct?: number;
  widthPct?: number;
  onClick?: (id: string) => void;
}

function AppointmentTileSimple({
  row,
  startHour,
  endHour,
  leftPct,
  widthPct,
  onClick,
}: AppointmentTileSimpleProps) {
  // Overlap geometry (PROD-982). `!= null` so leftPct === 0 positions at
  // `left: 0%` (Samer review §4/§5). Small FIXED 2px gutter on the right —
  // capped so it stays negligible on narrow columns at high column counts
  // (Samer review §2). Spans the column when not provided.
  const isOverlap = leftPct != null && widthPct != null;
  const posClass = isOverlap ? 'absolute' : 'absolute left-1 right-1';
  const overlapStyle = isOverlap
    ? { left: `${leftPct}%`, width: `calc(${widthPct}% - 2px)` }
    : null;
  const start = new Date(row.startISO);
  const end = new Date(row.endISO);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const gridStartMinutes = startHour * 60;
  const gridEndMinutes = endHour * 60;

  const top =
    ((Math.max(startMinutes, gridStartMinutes) - gridStartMinutes) / 60) *
    HOUR_HEIGHT;
  const height = Math.max(
    ((Math.min(endMinutes, gridEndMinutes) -
      Math.max(startMinutes, gridStartMinutes)) /
      60) *
      HOUR_HEIGHT,
    28,
  );

  if (endMinutes <= gridStartMinutes || startMinutes >= gridEndMinutes) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => onClick?.(row.id)}
      className={cn(
        posClass,
        'rounded-md border text-left pl-3 pr-2 py-1.5 shadow-sm hover:shadow-md transition-shadow pointer-events-auto overflow-hidden',
        'flex flex-col gap-1',
      )}
      style={{
        top: top + 4,
        height: Math.max(height - 8, 20),
        ...overlapStyle,
        backgroundColor: locationTileBg(row.profileLocation),
      }}
      // PHI gate (Samer review #90 §3): title attr is exposed to browser
      // tooltips, screen readers, and the accessibility tree. Keep it
      // PHI-FREE — only the time range + status. Patient + doctor names
      // remain in the visible tile body where the staff-only viewport
      // permission already gates exposure.
      title={`${row.timeRange} — ${row.statusDisplay}`}
    >
      {/* The category edge is the card's whole left shoulder, a 4 px colour bar clipped by the card's corners, not a left border bent by them (Antonin, 13 Oct). */}
      <span aria-hidden data-event-edge className="absolute inset-y-0 left-0 w-1" style={{ background: locationEdgeColor(row.profileLocation, row.profileColor) }} />
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-xs font-semibold text-text-primary truncate flex-1 min-w-0">
          {row.patientName}
        </span>
        {/* Importance badge + location icon (PROD-990). */}
        <AppointmentTileMeta
          location={row.profileLocation}
          locationName={row.locationName}
          profileColor={row.profileColor}
          importance={row.patientImportance}
          importanceDisplay={row.patientImportanceDisplay}
        />
      </div>
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-xs text-text-muted truncate flex-1">
          {row.timeRange} · {row.doctorName}
        </span>
        {row.status ? (
          <StatusBadge
            status={row.status as AppointmentStatusType}
            label={row.statusDisplay}
            className="text-xs px-1 py-px"
          />
        ) : null}
      </div>
    </button>
  );
}

function defaultHours(): number[] {
  return Array.from({ length: 10 }, (_, i) => i + 8);
}

// ─── grouping helper ──────────────────────────────────────────────────

/**
 * Bucket day-list rows by `row.room` (the friendly name). Rows whose
 * `room` is null, undefined, empty, OR whitespace-only are DROPPED —
 * they have no column to live in (Samer review #90 §1 — strict guard
 * prevents a silent "undefined" or " " catch-all column). Drift counts
 * are surfaced via `console.warn` in DEV so missing room assignments
 * upstream get caught early.
 *
 * Each bucket is sorted by start time for stable rendering.
 */
export function groupRowsByRoom(rows: DayListRow[]): Map<string, DayListRow[]> {
  const out = new Map<string, DayListRow[]>();
  let droppedNoRoom = 0;
  for (const row of rows) {
    const trimmed = row.room?.trim();
    if (!trimmed) {
      droppedNoRoom++;
      continue;
    }
    // Bucket under the trimmed key — preserves the case the backend
    // ships so existing callers see the same column name they expect.
    // Case-insensitive dedup happens later in resolveColumnRooms when
    // we union with the explicit `rooms` prop.
    const list = out.get(trimmed) ?? [];
    list.push(row);
    out.set(trimmed, list);
  }
  if (droppedNoRoom > 0 && import.meta.env.DEV) {
    // Dev-only signal; production stays silent.
    // eslint-disable-next-line no-console
    console.warn(
      `[ByRoomCalendar] groupRowsByRoom dropped ${droppedNoRoom} row(s) with no room — check the mapper.`,
    );
  }
  for (const list of out.values()) {
    list.sort((a, b) => a.startISO.localeCompare(b.startISO));
  }
  return out;
}

/**
 * Builds the column set rendered in the header strip. Resolution:
 *
 *   1. Start with the caller's `explicit` rooms (preserving order).
 *   2. Append any rooms from `derivedMap` not already represented
 *      (case-insensitive — "Room 1" and "room 1" collapse into the
 *      first occurrence; FHIR Location.name is user-entered).
 *   3. If both inputs are empty, return [].
 *
 * Exported for unit tests + so the page can compute the union once
 * for cross-cutting concerns (column count chips, etc.).
 */
export function resolveColumnRooms(
  explicit: string[] | undefined,
  derivedMap: Map<string, DayListRow[]>,
): string[] {
  const seen = new Set<string>(); // lowercase keys, for dedup
  const out: string[] = [];

  // Explicit list first (preserves caller order); each name is trimmed
  // before keying so " Room 1" doesn't dedup as a different room.
  for (const name of explicit ?? []) {
    const trimmed = name.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }

  // Derived rooms — sorted alphabetically, appended after the explicit
  // list so consumers can rely on stable order across renders.
  for (const room of Array.from(derivedMap.keys()).sort()) {
    const trimmed = room.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }

  return out;
}

// ─── Loading skeleton ─────────────────────────────────────────────────

function ByRoomCalendarSkeleton({ selectedDate, hours, columns }: { selectedDate: Date; hours: readonly number[]; columns: number }) {
  const dateLabel = selectedDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
  // The loaded grid's own frame (same header, gutter and hour rows), so nothing moves when the bookings land.
  return (
    <ColumnsCalendarSkeleton label={`By room · ${dateLabel}`} hours={hours} hourHeight={HOUR_HEIGHT} columns={columns} minColumnWidth={140} avatar={false} />
  );
}
